import { useEffect, useState } from "react";
import { FileSignature, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { api, errMsg } from "../lib/api";
import { parseAmount, toInputAmount } from "../lib/format";

const REASONS = [
  "Beleg verloren gegangen",
  "Kein Beleg ausgestellt (z. B. Automat, Parkschein, Trinkgeld)",
  "Aussteller nicht mehr erreichbar / existiert nicht mehr",
  "Rechnungskopie angefordert, aber nicht erhalten",
  "Sonstiges",
];

export const EigenbelegDialog = ({ tx, open, onOpenChange, onSaved }) => {
  const [f, setF] = useState(null);
  const [exists, setExists] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF(null);
    api.getEigenbeleg(tx.id).then((r) => { setExists(r.exists); setF({ ...r.data, amount: toInputAmount(r.data.amount) }); });
  }, [open, tx.id]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const field = (k, label, props = {}) => (
    <div className="grid gap-1">
      <Label htmlFor={`eb-${k}`} className="text-xs font-semibold text-slate-600">{label}</Label>
      <Input id={`eb-${k}`} data-testid={`eb-field-${k}`} value={f[k] ?? ""} onChange={set(k)} className="h-10 bg-white" {...props} />
    </div>
  );
  const area = (k, label) => (
    <div className="grid gap-1">
      <Label htmlFor={`eb-${k}`} className="text-xs font-semibold text-slate-600">{label}</Label>
      <Textarea id={`eb-${k}`} data-testid={`eb-field-${k}`} rows={2} value={f[k] ?? ""} onChange={set(k)} className="bg-white" />
    </div>
  );
  const submit = async () => {
    if (!f.issuer_name || !f.recipient_name || !f.description || !f.reason) return toast.error("Bitte Aussteller, Empfänger, Leistung und Grund ausfüllen.");
    setBusy(true);
    try {
      await api.saveEigenbeleg(tx.id, { ...f, amount: parseAmount(f.amount) ?? 0, date: f.date || null, sign_date: f.sign_date || null });
      toast.success(exists ? "Eigenbeleg aktualisiert" : "Eigenbeleg erstellt und verknüpft");
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="eigenbeleg-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FileSignature className="h-5 w-5" />Eigenbeleg (Ersatzbeleg) {exists ? "bearbeiten" : "erstellen"}</DialogTitle></DialogHeader>
        <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="eigenbeleg-hint">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          Eigenbelege werden vom Finanzamt nur ausnahmsweise anerkannt, wenn ein Originalbeleg nicht beschaffbar ist – bitte zuerst versuchen, eine Rechnungskopie anzufordern.
        </div>
        {!f ? <Loader2 className="h-5 w-5 animate-spin" /> : (
          <div className="grid gap-3 sm:grid-cols-2">
            {field("issuer_name", "Aussteller (Ihr Name) – wird gespeichert")}
            {area("issuer_address", "Adresse Aussteller")}
            {field("recipient_name", "Empfänger / Zahlungsempfänger")}
            {area("recipient_address", "Adresse Empfänger (falls bekannt)")}
            {field("date", "Datum der Zahlung / Leistung", { type: "date" })}
            <div className="grid grid-cols-[1fr_90px] gap-2">
              {field("amount", "Betrag", { className: "font-num h-10 bg-white" })}
              {field("currency", "Währung")}
            </div>
            <div className="sm:col-span-2">{area("description", "Art und Menge der Leistung")}</div>
            <div className="grid gap-1">
              <Label className="text-xs font-semibold text-slate-600">Grund, warum kein Originalbeleg vorliegt</Label>
              <select value={f.reason} onChange={set("reason")} data-testid="eb-field-reason" className="h-10 rounded-md border border-input bg-white px-2 text-sm">
                <option value="">Bitte wählen …</option>
                {REASONS.map((r) => <option key={r}>{r}</option>)}
              </select>
            </div>
            {field("reason_text", "Ergänzung (frei)")}
            {field("place", "Ort")}
            {field("sign_date", "Datum (Unterschrift)", { type: "date" })}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={submit} disabled={busy || !f} data-testid="eigenbeleg-save-btn" className="gap-1.5 bg-slate-900 hover:bg-slate-800">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}PDF {exists ? "neu erzeugen" : "erstellen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
